/**
 * fieldDoubleClick — a double-click inside a field on a graph canvas belongs to
 * the FIELD, never to the node or graph around it.
 *
 * Without this a dblclick on an input bubbles to whatever the node does on
 * dblclick: a Function Call node dives into the function, a State Machine opens
 * its inner graph, ReactFlow's onNodeDoubleClick selects/opens the node. Every
 * node fixing it with its own stopPropagation is how it got missed in the
 * first place, so it is one page-wide rule instead (installed from main.jsx):
 *
 *   - a field you can put a caret in (text / number / search … inputs,
 *     textareas, contenteditable) — its whole text is selected;
 *   - any other control (checkbox, select, button, slider, colour) — nothing
 *     extra happens, but the node doesn't see the double-click either.
 *
 * Scoped to `.react-flow` (every graph canvas: body, face, event, material,
 * state machine, transition rule) so double-click behaviour elsewhere in the
 * app is untouched. The listener runs on window in the CAPTURE phase and stops
 * the event there, so React's root listener — and through it every node
 * onDoubleClick and ReactFlow's onNodeDoubleClick — never receives it.
 */

// Input types you can type into / place a caret in.
const TEXT_INPUT_TYPES = new Set(['', 'text', 'number', 'search', 'email', 'url', 'tel', 'password'])

const CONTROL_SELECTOR = 'input, textarea, select, button, [contenteditable]:not([contenteditable="false"])'

/**
 * What a dblclick on `target` should do: 'select' (select the field's text and
 * stop), 'swallow' (just stop), or null (not ours — let it through).
 * The field found is returned alongside so the installer can act on it.
 */
export function fieldDoubleClickAction(target) {
  const el = target?.closest?.(CONTROL_SELECTOR)
  if (!el || !el.closest('.react-flow')) return { action: null, el: null }
  const tag = el.tagName
  if (tag === 'TEXTAREA') return { action: 'select', el }
  if (tag === 'INPUT') {
    const type = (el.getAttribute('type') ?? '').toLowerCase()
    // A number field being edited is temporarily type="text" (numericScrubInstall).
    return { action: TEXT_INPUT_TYPES.has(type) || TEXT_INPUT_TYPES.has(el.type) ? 'select' : 'swallow', el }
  }
  if (el.isContentEditable) return { action: 'select', el }
  return { action: 'swallow', el }
}

function selectAllIn(el) {
  if (el.isContentEditable && !('select' in el)) {
    const sel = el.ownerDocument.getSelection?.()
    if (!sel) return
    const range = el.ownerDocument.createRange()
    range.selectNodeContents(el)
    sel.removeAllRanges()
    sel.addRange(range)
    return
  }
  try { el.select() } catch { /* a type that refuses selection — nothing to select */ }
}

// Per window: a popped-out editor (FloatingWindow) is its own window and
// document, so it installs this again for itself.
const installed = new WeakSet()

export function installFieldDoubleClick(win = typeof window !== 'undefined' ? window : null) {
  if (!win || installed.has(win)) return
  installed.add(win)
  win.addEventListener('dblclick', (e) => {
    const { action, el } = fieldDoubleClickAction(e.target)
    if (!action) return
    e.stopPropagation()
    if (action === 'select') selectAllIn(el)
  }, true)
}
